import { TypedDocumentNode, gql } from '@apollo/client';
import { isPast } from 'date-fns';
import { useEffect, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import crest from '@sorare/core/src/assets/user/crest.png';
import Button from '@sorare/core/src/atoms/buttons/Button';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { RivalsLogo } from '@sorare/core/src/atoms/icons/RivalsLogo';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  BodyM,
  HeadlineL,
  HeadlineS,
  LabelL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { LANDING } from '@sorare/core/src/constants/__generated__/routes';
import {
  ONELINK_FOOTBALL_INVITE_REDIRECTION_BASE,
  ONELINK_FOOTBALL_RIVALS,
} from '@sorare/core/src/constants/externalLinks';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useQueryString from '@sorare/core/src/hooks/useQueryString';
import { glossary } from '@sorare/core/src/lib/glossary';
import { forwardParams } from '@sorare/core/src/lib/onelink';

import { GameTeamsMatchup } from '@sorare/gameplay/src/components/game/Game/GameTeamsMatchup';

import rivalsBackground from 'components/rivals/assets/rivalsBackground.svg';

import {
  RivalsInviteSignupQuery,
  RivalsInviteSignupQueryVariables,
} from './__generated__/index.graphql';

const StyledDialog = styled(Dialog)`
  background-image: linear-gradient(0deg, var(--c-neutral-100), transparent),
    url(${rivalsBackground});
`;
const Root = styled(Vertical)`
  padding: var(--double-unit);
  text-align: center;
`;
const ManagerPicture = styled.img`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  object-fit: cover;
  border-radius: 50%;
  border: 1px solid var(--c-rivals-friendlies);
`;

const RIVALS_INVITE_SIGNUP_QUERY = gql`
  query RivalsInviteSignupQuery($code: String!) {
    football {
      rivals {
        id
        lineup(code: $code) {
          id
          manager {
            id
            nickname
            pictureUrl
            user {
              slug
            }
          }
          game {
            slug
            game {
              id
              date
              ...GameTeamsMatchup_anyGameInterface
            }
          }
        }
      }
    }
  }
  ${GameTeamsMatchup.fragments.anyGameInterface}
` as TypedDocumentNode<
  RivalsInviteSignupQuery,
  RivalsInviteSignupQueryVariables
>;

type Props = {
  code: string;
  shouldRedirectToAppStore: boolean;
};

export const messages = {
  upcoming: defineMessages({
    headline: {
      id: 'RivalsInviteSignupDialog.headline',
      defaultMessage: 'Prove you know football best',
    },
    body: {
      id: 'RivalsInviteSignupDialog.body',
      defaultMessage:
        'Go head to head against friends and Rivals. Be rewarded for your passion!',
    },
    cta: {
      id: 'RivalsInviteSignupDialog.setLineup',
      defaultMessage: 'Set a lineup',
    },
  }),
  expired: defineMessages({
    headline: {
      id: 'RivalsInviteSignupDialog.expired.headline',
      defaultMessage: 'You missed the game!',
    },
    body: {
      id: 'RivalsInviteSignupDialog.expired.body',
      defaultMessage:
        'Prove you know football best and challenge your friends on another game',
    },
    cta: {
      id: 'RivalsInviteSignupDialog.expired.cta',
      defaultMessage: 'Find a match',
    },
  }),
};

export const RivalsInviteSignupDialog = ({
  code,
  shouldRedirectToAppStore,
}: Props) => {
  const { track } = useEventsContext();
  const navigate = useNavigate();
  const impactId = useQueryString('irclickid');
  const [, setSearchParams] = useSearchParams();
  const { signIn, signUp, closeConnectionDialog } = useConnectionContext();
  const [isOpen, setIsOpen] = useState(true);

  const { data, loading } = useQuery(RIVALS_INVITE_SIGNUP_QUERY, {
    variables: {
      code,
    },
  });

  const { game: rivalsGame, manager } = data?.football.rivals?.lineup || {};
  const game = rivalsGame?.game;

  const onClose = () => {
    setIsOpen(false);
    navigate(LANDING);
  };

  const openSignupOrSignin = (action: 'signin' | 'signup') => {
    setSearchParams({
      action,
      ...(manager?.user ? { referrer: manager?.user?.slug } : {}),
    });
    if (action === 'signin') {
      signIn();
    } else {
      signUp();
    }
  };

  // HACK :
  // When the url is containing `?action=` param
  // the dialogs are stacked depending on the order they are rendered,
  // this ensure the signup/signin dialog is mounted after this RivalsInviteSignupDialog
  // and consequently is shown above this dialog
  useEffect(() => {
    closeConnectionDialog();
  }, [closeConnectionDialog]);

  const isExpired = !game || isPast(game.date);

  const ctaUrl = isExpired
    ? ONELINK_FOOTBALL_RIVALS
    : forwardParams(
        ONELINK_FOOTBALL_INVITE_REDIRECTION_BASE,
        [code, impactId].filter(Boolean)
      );
  const contextualizedMessages = isExpired
    ? messages.expired
    : messages.upcoming;

  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      title={
        <HeadlineS as="h2">
          <FormattedMessage
            id="RivalsInviteSignupDialog.title"
            defaultMessage="You've been invited"
          />
        </HeadlineS>
      }
    >
      {!data && loading ? (
        <LoadingIndicator />
      ) : (
        <Root gap={8} center>
          <RivalsLogo width={128} />
          <Vertical gap={3}>
            <Vertical gap={2} center>
              <ManagerPicture src={manager?.pictureUrl || crest} alt="" />
              <LabelM as="span" color="var(--c-rivals-friendlies)">
                <FormattedMessage
                  id="RivalsInviteSignupDialog.challenge"
                  defaultMessage="{managerName} has challenged you"
                  values={{
                    managerName: manager?.nickname,
                  }}
                />
              </LabelM>
            </Vertical>
            {game && <GameTeamsMatchup game={game} />}
          </Vertical>

          <Vertical gap={4} center>
            <Vertical gap={2} center>
              <Vertical gap={1.5} center>
                <HeadlineL as="span">
                  <FormattedMessage {...contextualizedMessages.headline} />
                </HeadlineL>
                <BodyM as="span">
                  <FormattedMessage {...contextualizedMessages.body} />
                </BodyM>
              </Vertical>
              {shouldRedirectToAppStore ? (
                <Button
                  color="secondary"
                  size="medium"
                  href={ctaUrl}
                  onClick={() => {
                    if (!isExpired) {
                      track('Click Rivals Invite');
                    }
                  }}
                >
                  <FormattedMessage {...contextualizedMessages.cta} />
                </Button>
              ) : (
                <Button
                  color="secondary"
                  size="medium"
                  onClick={() => openSignupOrSignin('signup')}
                >
                  <FormattedMessage {...glossary.signup} />
                </Button>
              )}
            </Vertical>
            {!shouldRedirectToAppStore && (
              <Horizontal gap={0.5}>
                <LabelL as="span" color="var(--c-neutral-600)">
                  <FormattedMessage
                    id="RivalsInviteSignupDialog.alreadyHaveAccount"
                    defaultMessage="Have an account?"
                  />
                </LabelL>
                <ButtonBase onClick={() => openSignupOrSignin('signin')}>
                  <LabelL as="span">
                    <FormattedMessage {...glossary.signin} />
                  </LabelL>
                </ButtonBase>
              </Horizontal>
            )}
          </Vertical>
        </Root>
      )}
    </StyledDialog>
  );
};
